import { assign, entries, toJSON } from '@rehaje/shared'
import { sprintf } from 'sprintf-js'

import type {
  ErrorLocale,
  ErrorLocales,
  Locales,
  TypedErrorConstructor
} from '../types'

const defaultLanguage = navigator.language ?? 'zh-CN'
const localesStore: ErrorLocales = {}
const current = {
  language: defaultLanguage,
  locale: {} as ErrorLocale
}

export abstract class TypedError extends Error {
  /**
   * 内置语言包，必须使用 TypedError.registerErrorLocales 主动加载
   */
  static locales: Locales = {}
  args: string[] = []

  abstract override name: string

  override get message() {
    const format = current.locale[this.name]
    const args = format ? this.args : this.args.slice(1)
    return sprintf(format ?? this.args[0], ...args)
  }

  constructor(...args: string[]) {
    super()
    this.args = args
  }

  /**
   * 根据当前语言获取错误信息包
   */
  static __r_getLocale(): ErrorLocale {
    let language = current.language
    if (localesStore[language]) return localesStore[language]
    language = language.toLowerCase()
    if (localesStore[language]) return localesStore[language]
    language = language.split('-')[0] ?? language
    if (localesStore[language]) return localesStore[language]
    return localesStore[defaultLanguage] ?? {}
  }

  /**
   * 获取当前语言
   */
  static getLanguage(): string {
    return current.language
  }

  /**
   * 获取多语言包
   */
  static getLocales(): ErrorLocales {
    return toJSON(localesStore)
  }

  /**
   * 加载错误类内置语言包
   * @param errors 错误类映射
   *
   * @example
   * TypedError.registerErrorLocales({
   *   SchemaRequiredError
   * })
   */
  static registerErrorLocales(errors: Record<string, TypedErrorConstructor>) {
    const locales: ErrorLocales = {}
    for (const [errorName, errorConstructor] of entries(errors)) {
      if (!errorConstructor?.locales) continue
      for (const [localeName, localeMessage] of entries(
        errorConstructor.locales
      )) {
        locales[localeName] ??= {}
        locales[localeName][errorName] = localeMessage
      }
    }
    TypedError.setLocales(locales)
  }

  /**
   * 设置当前语言
   * @param language 语言
   */
  static setLanguage(language: string): void {
    current.language = language
    current.locale = this.__r_getLocale()
  }

  /**
   * 设置多语言包
   * @param locales 多语言包
   *
   * @example
   * TypedError.setLocales({
   *   'zh-CN': {
   *     SchemaRequiredError: '%s 字段必填'
   *   },
   *   'en-US': {
   *     SchemaRequiredError: '%s field is required'
   *   }
   * })
   */
  static setLocales(locales: ErrorLocales): void {
    for (const [key, value] of entries(locales)) {
      const locales = localesStore[key] ?? localesStore[key.toLowerCase()] ?? {}
      assign(locales, value)
      localesStore[key] = locales
      localesStore[key.toLowerCase()] = locales
    }
    current.locale = this.__r_getLocale()
  }

  /**
   * 设置多语言包别名映射
   * @param alias 别名映射
   *
   * @example
   * TypedError.setLocalesAlias({
   *   'zh-HK': 'zh-CN',
   *   'en-GB': 'en-US'
   * })
   */
  static setLocalesAlias(alias: Record<string, string>): void {
    for (const [key, value] of entries(alias)) {
      const locales =
        localesStore[value] ?? localesStore[value.toLowerCase()] ?? {}
      localesStore[key] = locales
      localesStore[key.toLowerCase()] = locales
    }
    current.locale = this.__r_getLocale()
  }
}
